import React from 'react'
import {
    PublicationCardItem,
    CardEvents,
    createCollectionAvailableEvent,
} from '@news-mono/web-common'
import { Lima } from '../../collections/Lima/Lima'
import { CollectionEvent } from '@news-mono/web-common'
import { themedValue } from '../../__styling/themed-value'
import { useTheme } from '@emotion/react'
import { LandscapeLayout } from '../../cards/Landscape/Landscape.layouts'
import { RenderTargetContext } from '@news-mono/web-common'
import { StyledAside } from './InlineRelatedContent.styled'
import { ResponsivePictureSizes } from '../Picture/responsive'
import { ImpressionAvailable } from '../../__helpers/impression-available-helper'
import { InlineRelatedContentCard } from './components/InlineRelatedContentCard'
import { ThemedRender } from '../../__styling/themed-render'
import { SevenNewsInlineRelatedContentCards } from './components/SevenNewsInlineRelatedContentCards'

export interface InlinerelatedContentProps {
    items: PublicationCardItem[]
    onEvent: (event: CardEvents | CollectionEvent) => void
}

export const InlineRelatedContent: React.FC<InlinerelatedContentProps> = ({
    items,
    onEvent,
}) => {
    const renderTarget = React.useContext(RenderTargetContext)

    const theme = useTheme()
    if (items.length === 0 || renderTarget.renderTarget === 'rss') {
        return null
    }

    const inlineContentSection =
        theme.section === 'community-news'
            ? ('inline-content-cng' as const)
            : ('inline-content' as const)

    const sectionHeadingPerthNow =
        theme.section === 'community-news'
            ? 'More from Community News'
            : 'More from PerthNow'

    const sectionHeader = themedValue(theme, {
        sevennews: undefined,
        thewest: { heading: 'More from The West', fontScale: 1 as const },
        perthnow: {
            heading: sectionHeadingPerthNow,
            fontScale: 1 as const,
            section: inlineContentSection,
        },
    })

    const landscapeCardLayout: LandscapeLayout = themedValue(theme, {
        thewest: {
            type: 'landscape',
            format: 'landscape-xsNoTeaser-mdTeaser-5050',
        },
        sevennews: {
            type: 'landscape',
            format: 'landscape-xsNoTeaser-mdTeaser-5050-simple',
        },
        perthnow: {
            type: 'landscape',
            format: 'landscape-inline-content-perthnow',
        },
    })

    const imageWidths = themedValue(theme, {
        thewest: {
            mobile: '110px',
            tablet: '369px',
            desktop: '315px',
            fallbackWidth: 315,
            mode: 'fixed',
        },
        perthnow: {
            mobile: '218px',
            tablet: '475px',
            desktop: '263px',
            fallbackWidth: 263,
            mode: 'fixed',
        },
        sevennews: {
            mobile: '215px',
            tablet: '345px',
            desktop: '275px',
            fallbackWidth: 275,
            mode: 'fixed',
        },
    }) as ResponsivePictureSizes

    if (items.length === 1) {
        return (
            <ImpressionAvailable
                loading={false}
                available={() => {
                    onEvent(
                        createCollectionAvailableEvent(
                            items,
                            'InlineRelatedContent',
                            {
                                componentType: 'inlinerelatedcontent',
                            },
                        ),
                    )
                }}
            >
                {(ref) => (
                    <StyledAside ref={ref}>
                        <ThemedRender
                            sevennews={() => (
                                <SevenNewsInlineRelatedContentCards
                                    onEvent={onEvent}
                                    expectedCards={items.length}
                                    items={{ loaded: true, result: items }}
                                />
                            )}
                            fallback={() => (
                                <Lima
                                    onEvent={onEvent}
                                    expectedCards={1}
                                    initialColumns={1}
                                    intermediateColumns={1}
                                    finalColumns={1}
                                    hideByline={true}
                                    hasBorder={false}
                                    verticalSpacing={'lg'}
                                    fixedRatios={['16:9']}
                                    sectionHeader={sectionHeader}
                                    cardLayout={[[landscapeCardLayout]]}
                                    items={{ loaded: true, result: items }}
                                    imageWidths={imageWidths}
                                    isInlineRelatedCollection={true}
                                />
                            )}
                        />
                    </StyledAside>
                )}
            </ImpressionAvailable>
        )
    }

    return (
        <ImpressionAvailable
            loading={false}
            available={() => {
                onEvent(
                    createCollectionAvailableEvent(
                        items,
                        'InlineRelatedContent',
                        {
                            componentType: 'inlinerelatedcontent',
                        },
                    ),
                )
            }}
        >
            {(ref) => (
                <StyledAside ref={ref}>
                    <ThemedRender
                        sevennews={() => (
                            <SevenNewsInlineRelatedContentCards
                                onEvent={onEvent}
                                expectedCards={items.length}
                                items={{ loaded: true, result: items }}
                            />
                        )}
                        fallback={() => (
                            <InlineRelatedContentCard
                                sectionHeader={sectionHeader}
                                onEvent={onEvent}
                                items={items}
                            />
                        )}
                    />
                </StyledAside>
            )}
        </ImpressionAvailable>
    )
}
InlineRelatedContent.displayName = 'InlineRelatedContent'
